import { useState, type FormEvent } from 'react'
import { ActionGlyph } from '../../components/ActionGlyph'
import { isValidUsername, normalizeUsername } from '../../domain/helpers'
import type { ActionResult } from '../../domain/models'
import { focusFirstInvalid } from '../../lib/forms'
import { useAppActions } from '../../state/AppProvider'
import { AccountScaffold, InlineStatus } from './AccountScaffold'

interface RegisterPageProps {
  navigate: (to: string) => void
}

interface RegisterErrors {
  realName?: string
  username?: string
  email?: string
  confirmEmail?: string
  password?: string
  consent?: string
}

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function RegisterPage({ navigate }: RegisterPageProps) {
  const actions = useAppActions()
  const [realName, setRealName] = useState('')
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [confirmEmail, setConfirmEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [consent, setConsent] = useState(false)
  const [errors, setErrors] = useState<RegisterErrors>({})
  const [result, setResult] = useState<ActionResult | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const nextErrors: RegisterErrors = {}
    if (realName.trim().length < 2) nextErrors.realName = 'Enter at least two characters.'
    if (!isValidUsername(username)) nextErrors.username = 'Use 3–24 letters, numbers, periods, or underscores.'
    if (!emailPattern.test(email.trim())) nextErrors.email = 'Enter a complete email address.'
    if (confirmEmail.trim().toLowerCase() !== email.trim().toLowerCase()) nextErrors.confirmEmail = 'Email addresses must match.'
    if (password.length < 8) nextErrors.password = 'Use at least eight characters.'
    if (!consent) nextErrors.consent = 'Choose this box to save the account on this device.'
    setErrors(nextErrors)
    setResult(null)
    if (Object.keys(nextErrors).length > 0) {
      focusFirstInvalid(form)
      return
    }

    setSubmitting(true)
    try {
      const nextResult = await actions.register({
        consent,
        realName: realName.trim(),
        username: normalizeUsername(username),
        email: email.trim(),
        password,
      })
      setResult(nextResult)
      if (nextResult.ok) {
        actions.completeEmailNextStep()
        navigate('/tickets/connect')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AccountScaffold
      eyebrow="Your account"
      title="Get on the list"
      intro="Save your ticket, show plan, city activity, and passport on this device."
      onBack={() => navigate('/welcome')}
    >
      <form className="access-form" noValidate onSubmit={handleSubmit} aria-busy={submitting}>
        <div className="access-field">
          <label htmlFor="register-real-name">First and last name</label>
          <input
            autoComplete="name"
            id="register-real-name"
            name="realName"
            value={realName}
            aria-describedby={errors.realName ? 'register-real-name-error' : 'register-real-name-hint'}
            aria-invalid={Boolean(errors.realName)}
            onChange={(event) => setRealName(event.target.value)}
          />
          {errors.realName ? (
            <p className="access-error" id="register-real-name-error">{errors.realName}</p>
          ) : (
            <p className="access-hint" id="register-real-name-hint">Used for your account. It is not shown in Community.</p>
          )}
        </div>

        <div className="access-field">
          <label htmlFor="register-username">Public username</label>
          <input
            autoCapitalize="none"
            autoComplete="username"
            id="register-username"
            maxLength={25}
            name="username"
            placeholder="Alextrextra"
            spellCheck={false}
            value={username}
            aria-describedby={errors.username ? 'register-username-error' : 'register-username-hint'}
            aria-invalid={Boolean(errors.username)}
            onChange={(event) => setUsername(event.target.value)}
          />
          {errors.username ? (
            <p className="access-error" id="register-username-error">{errors.username}</p>
          ) : (
            <p className="access-hint" id="register-username-hint">Shown publicly as @username on posts and replies.</p>
          )}
        </div>

        <div className="access-field">
          <label htmlFor="register-email">Email</label>
          <input
            autoCapitalize="none"
            autoComplete="email"
            id="register-email"
            inputMode="email"
            name="email"
            spellCheck={false}
            type="email"
            value={email}
            aria-describedby={errors.email ? 'register-email-error' : undefined}
            aria-invalid={Boolean(errors.email)}
            onChange={(event) => setEmail(event.target.value)}
          />
          {errors.email ? <p className="access-error" id="register-email-error">{errors.email}</p> : null}
        </div>

        <div className="access-field">
          <label htmlFor="register-confirm-email">Confirm email</label>
          <input
            autoCapitalize="none"
            autoComplete="email"
            id="register-confirm-email"
            inputMode="email"
            name="confirmEmail"
            spellCheck={false}
            type="email"
            value={confirmEmail}
            aria-describedby={errors.confirmEmail ? 'register-confirm-email-error' : 'register-confirm-email-hint'}
            aria-invalid={Boolean(errors.confirmEmail)}
            onChange={(event) => setConfirmEmail(event.target.value)}
          />
          {errors.confirmEmail ? (
            <p className="access-error" id="register-confirm-email-error">{errors.confirmEmail}</p>
          ) : (
            <p className="access-hint" id="register-confirm-email-hint">Re-enter the same address before creating your account.</p>
          )}
        </div>

        <div className="access-field">
          <label htmlFor="register-password">Password</label>
          <div className="access-password-field">
            <input
              autoComplete="new-password"
              id="register-password"
              minLength={8}
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              aria-describedby={errors.password ? 'register-password-error' : 'register-password-hint'}
              aria-invalid={Boolean(errors.password)}
              onChange={(event) => setPassword(event.target.value)}
            />
            <button
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              className="access-password-toggle"
              onClick={() => setShowPassword((current) => !current)}
              type="button"
            >
              <span aria-hidden="true" className="access-password-eye">
                <span />
              </span>
            </button>
          </div>
          {errors.password ? (
            <p className="access-error" id="register-password-error">{errors.password}</p>
          ) : (
            <p className="access-hint" id="register-password-hint">At least eight characters. Your password is not stored as readable text.</p>
          )}
        </div>

        <div>
          <label className="access-check">
            <input
              aria-describedby={errors.consent ? 'register-consent-error' : undefined}
              aria-invalid={Boolean(errors.consent)}
              checked={consent}
              id="register-consent"
              type="checkbox"
              onChange={(event) => {
                setConsent(event.target.checked)
                if (errors.consent) setErrors((current) => ({ ...current, consent: undefined }))
              }}
            />
            <span>I agree to save my account and activity on this device.</span>
          </label>
          {errors.consent ? <p className="access-error" id="register-consent-error">{errors.consent}</p> : null}
        </div>

        <InlineStatus result={result} />

        <div className="access-actions">
          <button className="access-primary" disabled={submitting} type="submit">
            {submitting ? 'Creating account…' : 'Create account'}
            <ActionGlyph />
          </button>
          <button className="access-text-button" type="button" onClick={() => navigate('/auth/sign-in')}>
            Already have an account? Sign in
          </button>
        </div>
      </form>
    </AccountScaffold>
  )
}
